import { computed, onMounted, ref, watch } from "vue";
import { useStore } from "@/store";
import { useI18n } from "vue-i18n";
import type { AddonsItemStruct, AddonType } from "@/struct/AddonsStruct";
import router from "@/router";

export function useAccount() {
  const store = useStore();
  const { t } = useI18n();

  const enabledOptions = ref<boolean>(true);
  const removeModal = ref<boolean>(false);
  const successModal = ref<boolean>(false);
  const errorModal = ref<boolean>(false);
  const promoModal = ref<boolean>(false);
  const feedbackModal = ref<boolean>(false);
  const feedbackResultModal = ref<boolean>(false);
  const feedbackResult = ref<boolean>(false);
  const pendingFeedbackVisit = ref<number | null>(null);
  const isAddonDetails = ref<boolean>(false);
  const chosenAddon = ref<AddonType>();

  const configDataBrand = computed(
    () => store.getters["brand/configDataBrand"]
  );
  const mainTariff = computed(() => store.getters["tariff/mainTariff"]);
  const addonOptions = computed(() => store.getters["tariff/addonOptions"]);
  const walletAmount = computed(() => store.getters["tariff/walletAmount"]);
  const tariffRenewalPrice = computed(
    () => store.getters["tariff/tariffRenewalPrice"]
  );
  const tariffFullPrice = computed(
    () => store.getters["tariff/tariffFullPrice"]
  );
  const tariffOptions = computed(() => store.getters["tariff/displayOptions"]);

  // Процент, а не рубли: 100 === "минус 100%".
  const discountPercent = computed(
    () => mainTariff.value?.discount?.percent || 0
  );
  const discountName = computed(() => mainTariff.value?.discount?.name || "");
  // "за месяц" / "за квартал" / "за полгода" / "за год" — решает duration (или isOfferAnnual как fallback).
  const pricePeriodLabel = computed(() =>
    t(mainTariff.value?.pricePeriodKey || "common.labels.perMonth")
  );
  // Зачёркнутую цену показываем только когда она действительно выше текущей.
  const hasDiscount = computed(
    () =>
      discountPercent.value > 0 &&
      tariffFullPrice.value > tariffRenewalPrice.value
  );
  const nextPaymentDate = computed(() => mainTariff.value?.nextDate || "");

  const servicesMenu = computed(() => [
    {
      title: t("account.services.premiumNumbers"),
      icon: "phone",
      link: "premium",
    },
    {
      title: t("account.services.roaming"),
      icon: "travel",
      link: "roaming",
    },
  ]);

  const updateSubscriptionDetails = async () => {
    await store.dispatch("tariff/updateSubscriptionDetails");
  };

  const toggleModal = (
    state: boolean,
    name: "remove" | "success" | "error" | "promo"
  ) => {
    switch (name) {
      case "remove":
        removeModal.value = state;
        break;
      case "error":
        errorModal.value = state;
        break;
      case "success":
        successModal.value = state;
        break;
      case "promo":
        promoModal.value = state;
        break;
    }
  };

  const openAddonDetails = (item: AddonsItemStruct) => {
    isAddonDetails.value = true;
    chosenAddon.value = {
      id: item.id as number,
      name: item.title,
      activation_price: item.amount as unknown as string,
      validityPeriod: 0,
      validityPeriodUnit: { id: 0, name: "" },
      productOptions: [],
    };
  };

  const disableAddon = () => {
    if (!chosenAddon.value) {
      toggleModal(false, "remove");
      toggleModal(true, "error");
      return;
    }
    store
      .dispatch("product/disableAddon", chosenAddon.value.id)
      .then((isSuccess: boolean) => {
        if (isSuccess) {
          toggleModal(true, "success");
        } else {
          toggleModal(true, "error");
        }
      })
      .finally(() => {
        toggleModal(false, "remove");
        isAddonDetails.value = false;
      });
  };

  const toPayment = () => {
    store.dispatch("system/togglePaymentModal", true);
  };

  const walletAmountCalc = computed(() => {
    const parsed = parseInt(walletAmount.value, 10);
    const value = Number.isNaN(parsed) ? 0 : parsed;
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  });

  const isLoading = computed(
    () => store.state.tariff.isLoadingSubscriptionDetails
  );
  const promoModalAction = () => {
    toggleModal(false, "promo");
    router.push({ path: "/tariff", query: { change: "true" } });
  };

  const handleFeedbackSubmitResult = (isSuccess: boolean) => {
    feedbackResult.value = isSuccess;
    feedbackResultModal.value = true;
    if (isSuccess) {
      store.dispatch("feedback/updateFeedbackSubmitted", true);
      pendingFeedbackVisit.value = null;
    }
  };

  const closeFeedbackResultModal = () => {
    feedbackResultModal.value = false;
  };

  watch(
    () => feedbackModal.value,
    (isOpen, wasOpen) => {
      if (!isOpen && wasOpen && pendingFeedbackVisit.value !== null) {
        store.dispatch(
          "feedback/addFeedbackShownVisit",
          pendingFeedbackVisit.value
        );
        pendingFeedbackVisit.value = null;
      }
    }
  );

  onMounted(async () => {
    await updateSubscriptionDetails();
    if (!store.state.system.promoModalShown) {
      store.dispatch("system/markPromoModalShown");
      promoModal.value = true;
    }
    const visitCount = store.getters.visitCount as number;
    const feedbackShownVisits = store.getters.feedbackShownVisits as number[];
    const feedbackSubmitted = store.getters.feedbackSubmitted as boolean;
    if (
      !feedbackSubmitted &&
      (visitCount === 3 || visitCount === 5 || visitCount === 7) &&
      !feedbackShownVisits.includes(visitCount)
    ) {
      feedbackModal.value = true;
      pendingFeedbackVisit.value = visitCount;
    }
  });

  return {
    addonOptions,
    chosenAddon,
    closeFeedbackResultModal,
    discountPercent,
    discountName,
    hasDiscount,
    disableAddon,
    enabledOptions,
    errorModal,
    feedbackModal,
    feedbackResult,
    feedbackResultModal,
    handleFeedbackSubmitResult,
    isAddonDetails,
    isLoading,
    nextPaymentDate,
    openAddonDetails,
    pricePeriodLabel,
    promoModal,
    promoModalAction,
    removeModal,
    servicesMenu,
    successModal,
    tariffOptions,
    tariffFullPrice,
    tariffRenewalPrice,
    toPayment,
    toggleModal,
    walletAmountCalc,

    configDataBrand,
  };
}
